import { ConditionalLink } from "components/conditionalLink";
import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { Switch } from "components/DesignSystem/Switch/Switch";
import { ArrowLeft } from "components/icons/ArrowLeft";
import { CircleWarning } from "components/icons/CircleWarning";
import { FolderOutline } from "components/icons/FolderOutline";
import { MenuDotsVertical } from "components/icons/MenuDotsVertical";
import { Search } from "components/icons/Search";
import { SquareHalf } from "components/icons/SquareHalf";
import {
  INKLE_CPA_TEAM_CHAT_TYPE,
  INKLE_FCA_TEAM_CHANNEL,
} from "constants/channelType";
import { useChatLocation } from "hooks/useChatLocation";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useGoBack } from "hooks/useGoBack";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useSelector } from "react-redux";
import { useGetTaskFromChannelIdQuery } from "store/apis/task";
import { RootState } from "store/store";
import {
  useChannelPreviewInfo,
  useChannelStateContext,
} from "stream-chat-react";
import { DefaultStreamChatGenerics } from "stream-chat-react/dist/types/types";

type MobileChannelHeaderProps = {
  isPrivateChannel: boolean;
  setPrivateChannel: () => void;
  showOnlyAdminMessage?: boolean;
  setShowOnlyAdminMessage: (val: boolean) => void;
  actionsAvailable?: boolean;
  isKBChannel: boolean;
  documentLink: string;
  pendingActionCount: number;
  onSearchClick: () => void;
  onDetailsClick: () => void;
  onPendingActionsClick: () => void;
};

export const MobileChannelHeader = <
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics
>({
  isPrivateChannel,
  setPrivateChannel,
  showOnlyAdminMessage,
  setShowOnlyAdminMessage,
  actionsAvailable = true,
  isKBChannel,
  documentLink,
  pendingActionCount,
  onSearchClick,
  onDetailsClick,
  onPendingActionsClick,
}: MobileChannelHeaderProps) => {
  const { channel } = useChannelStateContext<StreamChatGenerics>(
    "MobileChannelHeader"
  );
  const { displayTitle } = useChannelPreviewInfo({ channel });
  const { isCustomer } = useRoleBasedView();
  const { chatBasePath, section } = useChatLocation();
  const { link } = useConstructInternalLink();
  const inboxPath = section ? `${chatBasePath}/${section}` : chatBasePath;
  const goBack = useGoBack(link(inboxPath));
  const { showAIChannels } = useSelector(
    (state: RootState) => state.chatSearch
  );

  const { data: channelTask } = useGetTaskFromChannelIdQuery(
    { channelId: channel.id! },
    { skip: !channel.id }
  );

  const { tasks, company_group: companyGroup, entity } = channelTask || {};
  const task =
    tasks?.find(({ is_parent_task }) => is_parent_task) ?? tasks?.[0];
  const groupName = channel?.data?.group_name as string;

  const isNonGroupChannel = [
    INKLE_FCA_TEAM_CHANNEL,
    INKLE_CPA_TEAM_CHAT_TYPE,
  ].includes(channel?.data?.custom_type as string);

  const showPrivateToggle =
    !isCustomer && !isNonGroupChannel && !showAIChannels && !isKBChannel;
  const showAdminViewToggle = showAIChannels && !isKBChannel;

  const agentName =
    task?.assigned_to?.name || companyGroup?.assigned_agent?.name;

  const entityLink = isCustomer
    ? "/entities"
    : companyGroup?.uuid
    ? `/crm/${companyGroup.uuid}`
    : "";

  // Caption reads group · entity · assigned agent, company and entity link out
  const linkedCaptionParts = [
    !isCustomer ? groupName : undefined,
    entity?.name,
  ].filter(Boolean);

  const showActionsChip =
    actionsAvailable &&
    !showAIChannels &&
    !isKBChannel &&
    pendingActionCount > 0;

  return (
    <div className="t-flex t-flex-col t-border-0 t-border-b t-border-solid t-border-b-neutral-10 t-bg-white">
      {/* Row 1 — back, title (opens details), search + overflow */}
      <div className="t-flex t-items-center t-gap-2 t-px-4 t-py-3">
        <button
          className="all:unset t-flex t-items-center"
          onClick={goBack}
          aria-label="Back to chats"
        >
          <ArrowLeft size="16px" />
        </button>

        <button
          className="all:unset t-flex t-min-w-0 t-items-center"
          onClick={onDetailsClick}
        >
          <h2 className="t-m-0 t-truncate t-text-subtext-sm t-text-text-100">
            {displayTitle}
          </h2>
        </button>

        {showAdminViewToggle && (
          <Switch
            checked={Boolean(showOnlyAdminMessage)}
            onCheckedChange={setShowOnlyAdminMessage}
            size="small"
          />
        )}
        {showPrivateToggle && (
          <Switch
            checked={isPrivateChannel}
            onCheckedChange={setPrivateChannel}
            size="small"
          />
        )}

        <div className="t-ml-auto t-flex t-items-center t-gap-1">
          {actionsAvailable && !showAIChannels && !isKBChannel && (
            <Button
              size="small"
              customType="ghost_icon"
              onClick={onSearchClick}
            >
              <Search />
            </Button>
          )}
          <Dropdown.Root>
            <Dropdown.Trigger asChild>
              <Button size="small" customType="ghost_icon">
                <span className="t-text-text-30">
                  <MenuDotsVertical />
                </span>
              </Button>
            </Dropdown.Trigger>
            <Dropdown.Portal>
              <Dropdown.Content
                align="end"
                sideOffset={4}
                className="t-min-w-52"
              >
                <ConditionalLink to={documentLink}>
                  <Dropdown.Item icon={<FolderOutline size="16" />}>
                    Documents
                  </Dropdown.Item>
                </ConditionalLink>
                <Dropdown.Item icon={<SquareHalf />} onSelect={onDetailsClick}>
                  Details
                </Dropdown.Item>
              </Dropdown.Content>
            </Dropdown.Portal>
          </Dropdown.Root>
        </div>
      </div>

      {/* Row 2 — caption */}
      {(linkedCaptionParts.length > 0 || agentName) && (
        <div className="t-flex t-min-w-0 t-items-center t-gap-1 t-px-4 t-pb-2 -t-mt-1 t-text-body-sm t-text-text-30">
          {linkedCaptionParts.length > 0 && (
            <ConditionalLink
              to={entityLink}
              className="t-truncate t-text-body-sm !t-text-text-30 hover:!t-underline active:!t-underline"
            >
              {linkedCaptionParts.join(" · ")}
            </ConditionalLink>
          )}
          {linkedCaptionParts.length > 0 && agentName && <span>·</span>}
          {agentName && <span className="t-truncate">{agentName}</span>}
        </div>
      )}

      {/* Row 3 — actions chip */}
      {showActionsChip && (
        <div className="t-flex t-items-center t-justify-start t-gap-2 t-px-4 t-pb-3 t-pt-1">
          <button
            className="all:unset t-flex t-items-center t-gap-1.5 t-rounded-full t-border t-border-solid t-border-orange-30 t-bg-orange-0 t-px-3 t-py-1 t-text-subtext-sm t-text-orange-80 t-cursor-pointer"
            onClick={onPendingActionsClick}
          >
            <CircleWarning size={14} />
            {pendingActionCount > 99 ? "99+" : pendingActionCount} action
            {pendingActionCount > 1 ? "s" : ""}
          </button>
        </div>
      )}
    </div>
  );
};
